import React, { FC, useCallback, useRef } from 'react';
import { ConfigProvider } from 'antd';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import { selectBookingAppointmentData, selectBookingClientData } from '../../redux/slices/booking';
import { ClientData } from '../../model';
import useScrollToTop from '../../hooks/useScrollToTop';
import {
  BOOKING_STEPS, getBookingRedirect, getBookingStep, hasSelectedAppointment,
} from '../../utils/booking-flow';
import BookingProgress from '../../components/BookingProgress';
import BookingSummary from '../../components/BookingSummary';
import MobileStepHeader from '../../components/MobileStepHeader';
import { THANKS } from '../../constants/routes';
import { TELECARE_PHONE, TELECARE_PHONE_HREF } from '../../constants/contact';
import './uplift.css';

const BookingHome: FC = () => {
  const client = useSelector(selectBookingClientData) as ClientData;
  const appointment = useSelector(selectBookingAppointmentData);
  const { pathname, search } = useLocation();

  useScrollToTop();

  // antd renders modals, drawers, dropdowns and date panels at the end of <body> by default,
  // outside .bk and its styles; render them inside the booking page instead.
  const rootRef = useRef<HTMLDivElement>(null);
  const getPopupContainer = useCallback(() => rootRef.current ?? document.body, []);

  const redirect = getBookingRedirect({
    pathname,
    search,
    hasAppointment: hasSelectedAppointment(appointment),
    hasClient: !isEmpty(client),
  });
  if (redirect) {
    return <Navigate to={redirect} replace />;
  }

  const isConfirmation = pathname.replace(/\/+$/, '') === THANKS;
  const step = isConfirmation ? BOOKING_STEPS.length : getBookingStep(pathname);
  const inFlow = step !== undefined;

  return (
    <ConfigProvider getPopupContainer={getPopupContainer}>
      <div className="bk" ref={rootRef}>
        <header className="brandbar">
          <div className="in">
            <img src="/telecare-logo.svg" alt="Telecare" />
            <span className="help">
              {'Need help? '}
              <a href={TELECARE_PHONE_HREF}>{TELECARE_PHONE}</a>
            </span>
          </div>
        </header>
        {inFlow && <MobileStepHeader step={step} />}
        <div className="app">
          {inFlow && <div className="d-only"><BookingProgress current={step} /></div>}
          <div className={`cols${isConfirmation || !inFlow ? ' solo' : ''}`}>
            <main>
              <Outlet />
            </main>
            {inFlow && !isConfirmation && (
            <aside className="side" aria-label="Your appointment and fees">
              <BookingSummary />
            </aside>
            )}
          </div>
        </div>
      </div>
    </ConfigProvider>
  );
};

export default BookingHome;
